/**
 * "Mis reservas" state + polling (#106, cross-salon union #114) —
 * module-scope singleton.
 *
 * A vitrina page serves exactly ONE salon, so the state is app-global on
 * purpose (like usePseudoNotification): App.vue owns the polling lifecycle —
 * the simulated notification must fire wherever the customer is, menu open or
 * not, success screen included — and the CustomerMenu renders the same refs
 * without prop-drilling.
 *
 * Interval polling, deliberately — not Firebase Realtime (a second data path
 * plus SDK plus rules on the public surface for one list), not held-connection
 * long-polling (instance-hours on GAE by design). The edge pre-pays it with a
 * 20s memcache per identity, so the interval here is a UX number, not a cost
 * number, and the transport can change later without touching this contract.
 *
 * The list is the UNION by proven channels (api-salon#543): every salon this
 * identity booked at, each booking carrying `sid`, plus a salons map
 * {sid: {snm, tz, wa}}. The split is René's date rule — a booking goes to
 * Historial when its DATE is past in ITS salon's timezone; same-day past
 * hours stay upcoming. Two scopes ride the same endpoint: "active" (>= 48h
 * ago) is what polling refreshes; "history" loads ONCE, lazily, when the
 * Historial disclosure first expands.
 *
 * Transition detection is the point: a booking that WAS pending and IS
 * confirmed (or newly cancelled) fires `onTransition` — that drives the
 * simulated push. The baseline is the previous poll of THIS session; the
 * first load never notifies (everything would read as "new").
 */

import { computed, ref } from 'vue';
import { forgetContactToken } from '@/js/apis/contactToken';
import { fetchMyBookings } from '@/js/apis/myBookings';
import { isPastDate } from '@/js/utils/bookingDates';

// Booking wire statuses (api-salon BookingStatus — see models/booking.js).
export const BOOKING_STATUS = {
  CANCELLED: -1,
  PENDING: 0,
  CONFIRMED: 1,
  COMPLETED: 2,
  NO_SHOW: 3,
};

const POLL_MS = 30000;

const bookings = ref([]); // scope=active — what polling refreshes
const pastBookings = ref([]); // scope=history — loaded once, on demand
const salons = ref({}); // {sid: {snm, tz, wa}} merged from BOTH scopes
const currentSalonId = ref(null); // the storefront's salon — owns "Próximas"
const loading = ref(false);
const reason = ref(null); // null | 'no-session' | 'reverify' | 'unavailable'
const loaded = ref(false);
const historyLoading = ref(false);
const historyLoaded = ref(false);
const historyFailed = ref(false);

let pollTimer = null;
let onVisibility = null;
let baseline = null; // Map<bid, st> from the previous poll
let notify = null; // onTransition callback, set by startPolling
// Generation guard (Codex): an in-flight response from BEFORE a logout, a
// salon change, or a restart must not land — it would resurrect logged-out
// data or overwrite the new salon's state, and an out-of-order pair would
// regress the baseline into duplicate/missed notifications.
let generation = 0;
// Out-of-order belts WITHIN a generation (Codex on #114): a visibility tick
// and the interval can overlap, and the OLDER response landing last would
// overwrite the newer list and regress the baseline. Only the latest
// request of each kind may land.
let refreshSeq = 0;
let historySeq = 0;

/**
 * Drop everything the token identity owns. A session-level answer
 * (no-session / reverify) and clearData both come through here: on a shared
 * device the NEXT customer's token must never see the previous customer's
 * lists — pastBookings/salons/historyLoaded included (Codex on #114: they
 * survived before, and historyLoaded even refused the refetch).
 */
function dropSessionData() {
  bookings.value = [];
  pastBookings.value = [];
  salons.value = {};
  loaded.value = false;
  historyLoading.value = false;
  historyLoaded.value = false;
  historyFailed.value = false;
  baseline = null;
}

const bdtMs = (b) => {
  const ms = new Date(b.bdt || 0).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

/** The booking's OWN salon timezone — the union's salons map, by sid. */
function tzFor(booking) {
  const meta = salons.value[String(booking?.sid ?? '')];
  return meta?.tz || undefined;
}

const isCurrentSalon = (b) =>
  currentSalonId.value != null && String(b.sid ?? '') === String(currentSalonId.value);

// René's split: past DATE (salon-local) → Historial; everything else is
// upcoming, soonest first. Status stays a chip, not a bucket — a cancelled
// booking for tomorrow still shows under upcoming with its "Cancelada" pill.
const upcoming = computed(() =>
  bookings.value
    .filter((b) => isCurrentSalon(b) && !isPastDate(b.bdt, tzFor(b)))
    .sort((a, b) => bdtMs(a) - bdtMs(b))
);

const otherUpcoming = computed(() =>
  bookings.value
    .filter((b) => !isCurrentSalon(b) && !isPastDate(b.bdt, tzFor(b)))
    .sort((a, b) => bdtMs(a) - bdtMs(b))
);

// Historial = past-DATE bookings from BOTH scopes, all salons, newest first.
// The active scope reaches back 48h, so its yesterday-bookings land here too;
// dedup by bid in case a booking rides both answers around the boundary.
const past = computed(() => {
  const merged = new Map();
  // History FIRST, the polled active scope SECOND: for a bid riding both
  // answers in the 48h overlap, the POLLED copy is fresher — the one-time
  // history snapshot must not pin a stale status (Codex on #114).
  for (const b of [...pastBookings.value, ...bookings.value]) {
    if (isPastDate(b.bdt, tzFor(b))) merged.set(b.bid, b);
  }
  return [...merged.values()].sort((a, b) => bdtMs(b) - bdtMs(a));
});

// A booking that DISAPPEARS from the list deliberately does not notify: the
// list is capped upstream, so a slide past the cap would read as a
// cancellation that never happened — and a real cancellation stays in the
// list with its own status, which is the signal used here.
function detectTransitions(fresh) {
  if (!baseline || typeof notify !== 'function') return;
  for (const booking of fresh) {
    const before = baseline.get(booking.bid);
    if (before === undefined || before === booking.st) continue;
    if (before === BOOKING_STATUS.PENDING && booking.st === BOOKING_STATUS.CONFIRMED) {
      notify({ kind: 'confirmed', booking });
    } else if (booking.st === BOOKING_STATUS.CANCELLED) {
      notify({ kind: 'cancelled', booking });
    }
  }
}

// Single-flight (Codex on #112): the attention triggers (focus, menu open,
// visibility) can fire within milliseconds of each other. The sequence belt
// above already DROPS an out-of-order landing; this stops the duplicate
// REQUESTS from flying at all — one request in the air, extra triggers
// coalesce into a single trailing refresh.
let inFlight = false;
// The one trigger queued behind the in-flight request, stamped with the
// lifecycle (generation) that queued it — see the finally below.
let trailing = null;
let flightSeq = 0;

async function refresh(salonId) {
  if (!salonId) return;
  if (inFlight) {
    trailing = { salonId, generation };
    return;
  }
  inFlight = true;
  const flight = ++flightSeq;
  try {
    await refreshInner(salonId);
  } finally {
    // A straggler (a flight resetForTests forcibly abandoned) must not
    // release the slot a newer flight holds.
    if (flight === flightSeq) {
      inFlight = false;
      if (trailing) {
        const next = trailing;
        trailing = null;
        // The queued trigger runs only if the lifecycle that queued it is
        // still the current one (Codex on #138, two rounds): a trigger from
        // a torn-down session (stopPolling / salon change bumped the
        // generation AFTER it was queued) would fetch — and repopulate — for
        // a session that is gone; but a trigger queued BY the new lifecycle
        // (startPolling(B) while A is still in the air) is the new salon's
        // first load and must fly.
        if (next.generation === generation) refresh(next.salonId);
      }
    }
  }
}

/**
 * The EDGE answered `no-session`: the token this browser presents maps to
 * nothing there (a dev-database reset, a token revoked elsewhere — René's
 * localhost session, #136). Before this, the tab kept re-asking every 30 s
 * plus on every focus, and every answer was the same 401: a console full of
 * failures the visitor could not act on, for a session that was already gone.
 *
 * So: forget the dead token (never the GDID). That alone ends the churn —
 * from the next tick on, fetchMyBookings finds no token and returns before
 * any request, exactly as for a visitor who never had one. Polling itself
 * stays alive on purpose (Codex on #138): App owns that lifecycle and only
 * restarts it on a salon change, and a token-less tick is how the session
 * gets picked up the moment a booking mints a new token — stopping the poll
 * here would have left every visitor without live updates after their
 * first booking.
 *
 * `reason` stays 'no-session' — the header watches it to drop the account
 * entry. `reverify` is a different answer (the token is alive; a channel
 * needs re-attesting) and is left alone.
 */
function endDeadSession() {
  forgetContactToken();
}

/**
 * A session-level answer from the edge (no-session / reverify), whichever
 * request type brought it. refresh() is single-flight only against other
 * refreshes; loadHistory() can be in the air at the same time, on the same
 * generation, and its landing must not resurrect what this answer just ended
 * (Codex on #138, round 3): a history success landing after an active
 * no-session repopulated pastBookings/historyLoaded — which the NEXT customer
 * on a shared device would then inherit —, and an active success landing
 * after a history no-session repopulated the list and flipped the header back
 * on with no token behind it. So: void every request in the air (generation),
 * drop the queued trigger, clear both data domains, THEN record the answer —
 * and forget the token when it was the edge's no-session.
 *
 * The LOCAL no-session (no token in this browser) comes through here too
 * (Codex on #138, round 5): the token can vanish between two requests — a
 * logout in another tab clears the shared localStorage — while a history
 * load authenticated with it is still in the air, and that landing would
 * restore the previous customer's history for the next token to inherit.
 * Nothing to forget in that case (the token is already gone), and bumping
 * the generation on an anonymous visitor's tick costs nothing.
 */
function endSession(result) {
  generation += 1;
  trailing = null;
  dropSessionData();
  loading.value = false;
  reason.value = result.reason;
  if (result.reason === 'no-session' && !result.local) endDeadSession();
}

async function refreshInner(salonId) {
  currentSalonId.value = String(salonId);
  const gen = generation;
  const seq = ++refreshSeq;
  loading.value = !loaded.value;
  const result = await fetchMyBookings(salonId);
  // Stale (pre-logout/salon-change) or out-of-order (an older overlapping
  // poll landing after a newer one) — drop it whole.
  if (gen !== generation || seq !== refreshSeq) return;
  loading.value = false;
  if (!result.ok) {
    // 'unavailable' keeps the last good list on screen — a blip in a poll
    // must not blank the menu; a session-level answer replaces it.
    reason.value = result.reason;
    if (result.reason !== 'unavailable') endSession(result);
    return;
  }
  reason.value = null;
  loaded.value = true;
  detectTransitions(result.bookings);
  baseline = new Map(result.bookings.map((b) => [b.bid, b.st]));
  bookings.value = result.bookings;
  salons.value = { ...salons.value, ...result.salons };
  schedulePendingRetry(salonId);
}

// One coalesced retry just past the edge's 20s cache TTL (Codex on #112): an
// attention refresh can land INSIDE the cached window and receive the
// PENDIENTE answer written moments before the salon confirmed — and the
// interval would not look again for up to 30s. While something is PENDING
// and the polling lifecycle is alive, one extra look after the cache expires
// bounds the visible staleness to ~TTL. Coalesced (never stacks) and
// visibility-gated at fire time; a list with nothing pending schedules
// nothing, so steady-state cost is unchanged.
const PENDING_RETRY_MS = 21000;
let pendingRetryTimer = null;

function schedulePendingRetry(salonId) {
  if (!pollTimer || pendingRetryTimer) return;
  if (!bookings.value.some((b) => b.st === BOOKING_STATUS.PENDING)) return;
  pendingRetryTimer = setTimeout(() => {
    pendingRetryTimer = null;
    if (document.visibilityState === 'visible') refresh(salonId);
  }, PENDING_RETRY_MS);
}

/**
 * Lazy history (#114): fetched ONCE, when the Historial disclosure first
 * expands — "Leyendo reservas pasadas…" while it runs. A failure arms a
 * retry (the next call re-fetches) instead of caching an empty answer.
 */
async function loadHistory(salonId) {
  if (!salonId || historyLoading.value || historyLoaded.value) return;
  const gen = generation;
  const seq = ++historySeq;
  historyLoading.value = true;
  historyFailed.value = false;
  const result = await fetchMyBookings(salonId, 'history');
  if (seq !== historySeq) return; // a newer history load owns the flags now
  if (gen !== generation) {
    // Superseded by a logout/salon change mid-flight. Unwedge the flag
    // (Codex on #114: it stayed true forever, and every later expand
    // no-oped) — the data refs were already reset with the generation.
    historyLoading.value = false;
    return;
  }
  historyLoading.value = false;
  if (!result.ok) {
    if (result.reason === 'reverify' || result.reason === 'no-session') {
      // Session-level answer: the menu's reverify UI takes over — a generic
      // history retry could never succeed (Codex on #114), and the identity
      // that failed must not keep its cached lists either — nor may an
      // active refresh still in the air bring them back (endSession).
      endSession(result);
    } else {
      historyFailed.value = true;
    }
    return;
  }
  historyLoaded.value = true;
  pastBookings.value = result.bookings;
  salons.value = { ...salons.value, ...result.salons };
}

/**
 * Poll while the tab is visible. The visibility guard is the cost belt: a
 * backgrounded tab polls nothing, and the moment the tab is foregrounded
 * again it refreshes immediately — that is exactly when the customer looks.
 *
 * Window FOCUS refreshes too (#112, René's QA): with the vitrina and the
 * salon app in side-by-side WINDOWS, the vitrina tab never changes
 * visibility — visibilitychange alone left him staring at PENDIENTE for the
 * full 30s interval after the salon confirmed. Focus fires on the window
 * switch; the visibility check inside tick keeps it a no-op when hidden,
 * and the edge's 20s cache stays the cost ceiling under rapid switching.
 */
function startPolling(salonId, { onTransition } = {}) {
  stopPolling();
  generation += 1;
  notify = onTransition || null;
  const tick = () => {
    if (document.visibilityState === 'visible') refresh(salonId);
  };
  onVisibility = tick;
  document.addEventListener('visibilitychange', tick);
  window.addEventListener('focus', tick);
  pollTimer = setInterval(tick, POLL_MS);
  refresh(salonId);
}

function stopPolling() {
  generation += 1; // any in-flight refresh is now void
  trailing = null; // and so is anything queued behind it
  if (onVisibility) {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('focus', onVisibility);
    onVisibility = null;
  }
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (pendingRetryTimer) {
    clearTimeout(pendingRetryTimer);
    pendingRetryTimer = null;
  }
  notify = null;
}

/**
 * Drop the session's data (logout): the lists, the baseline, the reason.
 * Polling is deliberately untouched — App owns that lifecycle, and a
 * token-less tick costs nothing (fetchMyBookings returns before the fetch).
 */
function clearData() {
  generation += 1; // an in-flight authenticated response must not repopulate
  dropSessionData();
  loading.value = false;
  reason.value = null;
}

/** Test hook: back to a pristine module state. */
function resetForTests() {
  stopPolling();
  clearData();
  currentSalonId.value = null;
  // Single-flight state too: a straggler from a previous test must not make
  // the next test's first refresh coalesce into nothing.
  inFlight = false;
  trailing = null;
}

export function useMyBookings() {
  return {
    bookings,
    salons,
    upcoming,
    otherUpcoming,
    past,
    loading,
    loaded,
    reason,
    historyLoading,
    historyLoaded,
    historyFailed,
    refresh,
    loadHistory,
    startPolling,
    stopPolling,
    clearData,
    resetForTests,
  };
}
